import React from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import { AsQ, ParagraphSM, TitleXS } from '@/atoms/Text'
import { useProjectMeta } from '@/contexts/ProjectMetaContext'
import { useTheatricalRatings } from '@/hooks/useTheatricalRatings'
import { HubpageSingleItemSlider } from '@/molecules/HubpageSingleItemSlider'
import { MovieRating } from '@/molecules/MovieRating'
import { MovieRatingsContainer } from '@/organisms/MovieRatingsContainer'
import { ContentfulCloudinaryImageType } from '@/services/EmailCapturePageService/EmailCapturePageService'
import { TheatricalReleaseObject } from '@/types/codegen-federation'
import { useTranslate } from '@/utils/translate/translate-client'
import { useWatchTheatricalContext } from '../WatchTheatricalContext'

interface ReviewsProps {
  theatricalRelease?: TheatricalReleaseObject | null
}

export const Reviews: React.FC<ReviewsProps> = ({ theatricalRelease }) => {
  const { t } = useTranslate('watch')
  const { rottenTomatoes, audience, cinema, critics, IMDb } = useWatchTheatricalContext()
  const projectMeta = useProjectMeta()

  const {
    rottenTomatoes: rottenTomatoesRating,
    audience: audienceRating,
    cinema: cinemaRating,
    imdb: IMDbRating,
    shouldShowRottenTomatoes,
    shouldShowAudience,
    shouldShowCinema,
    shouldShowImdb,
  } = useTheatricalRatings(theatricalRelease, {
    rottenTomatoes: rottenTomatoes?.items[0],
    audience: audience?.items[0],
    cinema: cinema?.items[0],
    imdb: IMDb?.items?.[0],
  })

  const { projectType } = projectMeta

  return (
    <div
      className={classNames('grid bg-black text-white', {
        'md:grid-cols-[250px_1fr]': critics?.items && critics.items.length > 0,
      })}
    >
      {critics?.items && critics.items.length > 0 && (
        <HubpageSingleItemSlider
          title={
            projectType === 'movie'
              ? t('movieReviews', 'Movie Reviews')
              : projectType === 'series'
              ? t('seriesReviews', 'Series Reviews')
              : t('reviews', 'Reviews')
          }
          className="flex w-full flex-col items-center justify-center bg-black text-white"
          sliderStyles="overflow-hidden w-full mx-auto"
          id="review-section-title"
        >
          {critics?.items?.map((review, index: number) => (
            <div
              key={index}
              className="my-2 !flex min-h-[120px] max-w-full place-content-center items-center rounded-lg !bg-core-gray-950 text-center md:min-h-[300px]"
            >
              <article className="px-8 py-4">
                <TitleXS
                  as={AsQ}
                  className="md:photon-title-sm lg:photon-heading-xs my-2 text-pretty"
                  weight="bold"
                >{`${review?.quote}`}</TitleXS>
                <ParagraphSM>
                  {review?.author}
                  {review?.company && `- ${review.company}`}
                </ParagraphSM>
              </article>
            </div>
          ))}
        </HubpageSingleItemSlider>
      )}
      <MovieRatingsContainer className="max-w-[200px]">
        <When condition={shouldShowRottenTomatoes}>
          <MovieRating
            icon={(rottenTomatoesRating?.icon as ContentfulCloudinaryImageType[] | undefined)?.[0]}
            rating={rottenTomatoesRating?.rating}
            author={rottenTomatoesRating?.author}
          />
        </When>
        <When condition={shouldShowAudience}>
          <MovieRating
            icon={(audienceRating?.icon as ContentfulCloudinaryImageType[] | undefined)?.[0]}
            rating={audienceRating?.rating}
            author={audienceRating?.author}
          />
        </When>
        <When condition={shouldShowCinema}>
          <MovieRating
            icon={(cinemaRating?.icon as ContentfulCloudinaryImageType[] | undefined)?.[0]}
            rating={cinemaRating?.rating}
            author={cinemaRating?.author}
          />
        </When>
        <When condition={shouldShowImdb}>
          <MovieRating
            icon={(IMDbRating?.icon as ContentfulCloudinaryImageType[] | undefined)?.[0]}
            rating={IMDbRating?.rating}
            author={IMDbRating?.author}
          />
        </When>
      </MovieRatingsContainer>
    </div>
  )
}
